// launch-redesign T34 (E17): the typed-reason waive form. The submit button stays disabled until the trimmed
// reason has `minLength` characters (the proxy enforces the same 10 and answers 400 reason_too_short).
// All copy from utils/launchWording.js; colours are App.css tokens. Visual proof is Manual M5.
import React, { useState } from 'react';
import { UI_WORDING, fill } from '../../utils/launchWording';

/** reasonReady(text, minLength) -> boolean. Trimmed length, so whitespace cannot satisfy the gate. */
export const reasonReady = (text, minLength = 10) => String(text == null ? '' : text).trim().length >= minLength;

/**
 * <WaiveDialog onSubmit minLength={10} />
 * onSubmit(reason: string) - called with the trimmed reason; may return a promise (the button shows busy
 * until it settles). Optional: onCancel, target { criterion, unit }, error (string shown under the field).
 */
export default function WaiveDialog({ onSubmit, onCancel, minLength = 10, target, error }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = reasonReady(reason, minLength);

  async function submit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try { await onSubmit(reason.trim()); } finally { setBusy(false); }
  }

  return (
    <form
      role="dialog"
      aria-label={UI_WORDING.waiveTitle}
      onSubmit={submit}
      style={{ background: 'var(--card)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 6, padding: 16, maxWidth: 520 }}
    >
      <div style={{ fontWeight: 700 }}>{UI_WORDING.waiveTitle}</div>
      {target && (
        <div style={{ color: 'var(--muted)', fontSize: 12 }}>{fill(UI_WORDING.waiveTarget, { criterion: target.criterion, unit: target.unit })}</div>
      )}
      <p style={{ fontSize: 13 }}>{UI_WORDING.waiveExplain}</p>
      <label style={{ display: 'block', fontSize: 13 }}>
        {UI_WORDING.waiveReasonLabel}
        <textarea
          aria-label={UI_WORDING.waiveReasonLabel}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          style={{ display: 'block', width: '100%', background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line)' }}
        />
      </label>
      <div style={{ color: 'var(--muted)', fontSize: 12 }}>
        {fill(UI_WORDING.waiveReasonHelp, { n: minLength, count: reason.trim().length })}
      </div>
      {error && <div role="alert" style={{ color: 'var(--error-text)', fontSize: 13 }}>{error}</div>}
      <div style={{ marginTop: 8 }}>
        <button type="submit" disabled={!ready || busy}>{busy ? UI_WORDING.waiveSubmitting : UI_WORDING.waiveSubmit}</button>
        {onCancel && <button type="button" onClick={onCancel} style={{ marginLeft: 8 }}>{UI_WORDING.waiveCancel}</button>}
      </div>
    </form>
  );
}
